import { useState, useCallback } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Button,
  Badge,
  Spinner,
  Modal,
  List,
  Box,
  Icon,
} from '@shopify/polaris';
import { QuestionCircleIcon, CheckIcon, AlertTriangleIcon } from '@shopify/polaris-icons';
import { useQuery } from '@tanstack/react-query';
import { useFeedStatus } from '../hooks/useFeed';
import { checkFeedUrls } from '../services/api';
import type { UrlCheckResponse } from '../services/api';
import { EnableEmbedVideoLink } from './EnableEmbedVideoModal';

/**
 * App Embed & Feed URL status card for the dashboard.
 *
 * 1. Calls GET /api/feed/url-check (server-side probe — no CORS issues).
 * 2. Shows warning if store domain feed URLs are not reachable.
 * 3. Help icon opens a modal with setup instructions.
 */
export function AppEmbedStatusCard() {
  const { data: feed } = useFeedStatus();
  const [helpOpen, setHelpOpen] = useState(false);

  const feedActive = feed && feed.status === 'ACTIVE';

  const {
    data: urlCheck,
    isLoading: isChecking,
    refetch,
  } = useQuery<UrlCheckResponse>({
    queryKey: ['feed-url-check'],
    queryFn: () => checkFeedUrls(),
    enabled: !!feedActive,
    staleTime: 60_000,
    retry: false,
  });

  const handleRecheck = useCallback(() => {
    refetch();
  }, [refetch]);

  // Don't render anything if feed isn't activated yet
  if (!feedActive) return null;

  const embedDetected = urlCheck?.embedDetected ?? null;
  const checks = urlCheck?.checks ?? [];
  const allOk = checks.length > 0 && checks.every((c) => c.ok);

  return (
    <>
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h2" variant="headingMd">Store Integration</Text>
              {embedDetected === true && <Badge tone="success">Active</Badge>}
              {embedDetected === false && <Badge tone="critical">Not detected</Badge>}
              {isChecking && <Spinner size="small" />}
            </InlineStack>
            <Button
              variant="plain"
              icon={QuestionCircleIcon}
              onClick={() => setHelpOpen(true)}
              accessibilityLabel="Help with App Embed setup"
            />
          </InlineStack>

          {/* Embed not enabled warning */}
          {embedDetected === false && (
            <Banner tone="warning">
              <p>
                <strong>App embed not detected.</strong> Your store’s HTML does not
                contain Zelium link tags. Enable the <strong>Zelium AI Structured Data</strong> app
                embed in your theme to make your products discoverable by AI crawlers.
              </p>
              {urlCheck?.embedDetail && (
                <p>
                  <Text as="span" variant="bodySm" tone="subdued">
                    Detail: {urlCheck.embedDetail}
                  </Text>
                </p>
              )}
              <EnableEmbedVideoLink />
            </Banner>
          )}

          {/* All checks passed */}
          {allOk && (
            <Banner tone="success">
              All store domain feed URLs are accessible. AI crawlers can discover your products.
            </Banner>
          )}

          {/* Individual URL check results */}
          {checks.length > 0 && (
            <BlockStack gap="200">
              {checks.map((check) => (
                <InlineStack key={check.label} gap="200" blockAlign="center" wrap={false}>
                  {check.ok ? (
                    <Box>
                      <Icon source={CheckIcon} tone="success" />
                    </Box>
                  ) : (
                    <Box>
                      <Icon source={AlertTriangleIcon} tone="critical" />
                    </Box>
                  )}
                  <Text as="span" variant="bodySm" tone={check.ok ? undefined : 'critical'}>
                    {check.label}
                  </Text>
                  {!check.ok && check.status > 0 && (
                    <Badge tone="critical">{String(check.status)}</Badge>
                  )}
                </InlineStack>
              ))}
            </BlockStack>
          )}

          {/* Re-check button */}
          {!isChecking && (
            <InlineStack>
              <Button onClick={handleRecheck} size="slim">
                Re-check
              </Button>
            </InlineStack>
          )}
        </BlockStack>
      </Card>

      {/* Help Modal */}
      <Modal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="How to Enable App Embed"
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p" variant="bodyMd">
              The Zelium AI app embed injects structured data (JSON-LD), feed links,
              and llms.txt links into your store&apos;s HTML. This makes your products
              discoverable by AI platforms like ChatGPT, Perplexity, and Google AI.
            </Text>

            <Text as="h3" variant="headingSm">Setup Steps</Text>
            <List type="number">
              <List.Item>
                Go to <strong>Online Store → Themes</strong> in your Shopify admin.
              </List.Item>
              <List.Item>
                Click <strong>Customize</strong> on your active theme.
              </List.Item>
              <List.Item>
                In the theme editor, click <strong>App embeds</strong> (puzzle piece icon
                in the left sidebar).
              </List.Item>
              <List.Item>
                Find <strong>Zelium AI Structured Data</strong> and toggle it <strong>on</strong>.
              </List.Item>
              <List.Item>
                Click <strong>Save</strong> in the top right.
              </List.Item>
            </List>

            <Text as="h3" variant="headingSm">What Gets Injected</Text>
            <List>
              <List.Item>
                <strong>JSON-LD structured data</strong> on product pages — enriched
                schema.org/Product markup with AI summaries, identifiers, and quality scores.
              </List.Item>
              <List.Item>
                <strong>Feed link tag</strong> — a{' '}
                <code>&lt;link rel=&quot;alternate&quot;&gt;</code> pointing to your AI product feed.
              </List.Item>
              <List.Item>
                <strong>llms.txt link tag</strong> — a{' '}
                <code>&lt;link rel=&quot;author&quot;&gt;</code> pointing to your llms.txt file
                for AI crawler discovery.
              </List.Item>
            </List>

            <Text as="h3" variant="headingSm">Troubleshooting</Text>
            <List>
              <List.Item>
                <strong>URLs return 404:</strong> Make sure the app embed is enabled and
                your feed has been activated and generated at least once.
              </List.Item>
              <List.Item>
                <strong>CORS errors in browser console:</strong> This is expected for
                cross-origin requests. AI crawlers don&apos;t enforce CORS and can access
                the URLs normally. Use the store feed URLs on the Feed page as
                a fallback for browser testing.
              </List.Item>
              <List.Item>
                <strong>Blank JSON-LD:</strong> Ensure storefront injection is enabled
                (Feed page → Activate) and products have been scanned.
              </List.Item>
            </List>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
